import {Suspense, useEffect, useMemo, useRef} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {ThreadPreview} from './ThreadPreview'
import {ThreadPreviewSkeleton} from './ThreadPreviewSkeleton'
import {CommentsErrorFallback} from './CommentsErrorFallback'
import {HydrateThreadPositioning} from './HydrateThreadPositioning'
import {announce} from '@github-ui/aria-live'
import {debounce} from '@github/mini-throttle'

export interface ThreadDetailContentProps {
  selectedThread: Thread
  onNavigateToDiffComment?: () => void
}

/**
 * Container that renders thread detail with replies.
 */
export function ThreadDetailContent({selectedThread, onNavigateToDiffComment}: ThreadDetailContentProps) {
  const threadsToHydrate = useMemo(() => [selectedThread], [selectedThread])

  return (
    <>
      <HydrateThreadPositioning threads={threadsToHydrate} />
      <ThreadDetailComponent selectedThread={selectedThread} onNavigateToDiffComment={onNavigateToDiffComment} />
    </>
  )
}

/**
 * Presentational thread previews renderer. It announces result counts, shows zero state
 * for empty filtered results, and switches to a single selected thread in detail mode.
 */
function ThreadDetailComponent({selectedThread, onNavigateToDiffComment}: ThreadDetailContentProps) {
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])
  const announceRef = useRef<HTMLDivElement>(null)

  const threadCommentsLength = selectedThread.commentsData?.comments?.length
  useEffect(() => {
    const element = announceRef.current as HTMLElement

    if (threadCommentsLength) {
      debouncedAnnounce(`${threadCommentsLength} thread comments`, {element})
      return
    }
  }, [debouncedAnnounce, threadCommentsLength])

  return (
    <>
      <ErrorBoundary boundaryName="DockedThreadPreviewsContent" fallback={<CommentsErrorFallback />}>
        <Suspense fallback={<ThreadPreviewSkeleton />}>
          <div className="d-flex flex-column position-relative width-full gap-3">
            <ThreadPreview
              detailMode
              thread={selectedThread}
              selectedReviewId={null}
              onNavigateToDiffComment={onNavigateToDiffComment}
              headingLevel="h2"
            />
          </div>
        </Suspense>
      </ErrorBoundary>
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}
